import * as Sentry from '@sentry/react'
import { NodeApi } from 'platejs'

import { withoutSavingHistory } from '~publish/legacy/editor/BufferEditor/history'
import type { EditorNodesOptions } from 'platejs'
import { Node } from 'slate'
import { DOMEditor } from 'slate-dom'

import {
  deserialize,
  getPlainText as getSlatePlainText,
  serialize,
} from '~publish/legacy/editor/BufferEditor'
import type {
  BufferEditor,
  BufferValue,
} from '~publish/legacy/editor/BufferEditor/types.plate'
import type { EditorEntityData } from '~publish/legacy/editor/helpers/EditorEntityData'
import { getAllFacebookMentions } from '~publish/legacy/editor/plugins/facebook-mention/commands/getAllFacebookMentions'
import type { FacebookMentionDbEntry } from '~publish/legacy/editor/plugins/facebook-mention/nodes'
import { getAllLinkNodes } from '~publish/legacy/editor/plugins/link/nodes/getAllLinkNodes'
import { LinkElement } from '~publish/legacy/editor/plugins/link/nodes/LinkElement'
import type {
  LinkedInAnnotationEntity,
  LinkedInAnnotationEntityDetails,
} from '~publish/legacy/editor/plugins/linkedin-annotations/nodes'
import { isSelectionAtEndOfCurrentNode } from '~publish/legacy/editor/queries/isSelectionAtEndOfNode'

import type { SlateDraft } from '../Draft'

// slate-react throws this exact prefix when it cannot map a Slate node to a
// mounted DOM element. See `toDOMNode` in slate-react's ReactEditor.
const UNRESOLVED_DOM_NODE_ERROR = 'Cannot resolve a DOM node from Slate node'

function isUnresolvedDomNodeError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.message.startsWith(UNRESOLVED_DOM_NODE_ERROR)
  )
}

function isEditorOrState(
  editorOrState: BufferEditor | unknown,
): editorOrState is BufferEditor {
  return NodeApi.isEditor(editorOrState)
}

function createStateFromText(
  editor: BufferEditor,
  text = '',
  data?: EditorEntityData,
): BufferEditor {
  withoutSavingHistory(editor, () => {
    deserialize(editor, text, data)
  })

  return editor
}

export type GetPlainTextOptions = EditorNodesOptions<BufferValue>
function getPlainText(
  editor: BufferEditor,
  options: GetPlainTextOptions = {},
): string {
  return getSlatePlainText(editor, options)
}

function hasText(
  editor: BufferEditor,
  options: EditorNodesOptions<BufferValue> = {},
): boolean {
  return !!getSlatePlainText(editor, options).trim()
}

function hasUnshortenedLinks(editor: BufferEditor): boolean {
  return getAllLinkNodes(editor).some(([node]) =>
    LinkElement.hasBeenUnshortened(node),
  )
}

function parseLinks(editor: BufferEditor): string[] {
  const nodes = getAllLinkNodes(editor)

  return nodes.map(([node]) => LinkElement.getText(node))
}

type InsertLinkedInAnnotation = (
  editor: BufferEditor,
  annotation: LinkedInAnnotationEntityDetails,
) => void

const isInsertLinkedInAnnotation = (
  value: unknown,
): value is InsertLinkedInAnnotation => typeof value === 'function'

function copyDraftEditorState(
  draftFrom: SlateDraft,
  draftTo: SlateDraft,
): BufferEditor {
  const { text, data } = serialize(draftFrom.editorState)
  return createStateFromText(draftTo.editorState, text, data)
}

function getFacebookAutocompleteEntities(
  editor: BufferEditor,
): FacebookMentionDbEntry[] {
  return getAllFacebookMentions(editor)
}

/**
 * Flattens the editor's node tree into a compact path→type record
 * for error reporting. Strips all content, preserving only
 * where each node sits and what type it is.
 *
 * Example output: { "0": "p", "0.0": "text", "1": "p", "1.0": "a", "1.0.0": "text" }
 */
function flattenEditorStructure(
  editor: BufferEditor | undefined,
): Record<string, string> {
  if (!editor?.children) return {}

  const structure: Record<string, string> = {}
  for (const [node, path] of Node.descendants(editor)) {
    structure[path.join('.')] = 'type' in node ? String(node.type) : 'text'
  }
  return structure
}

export const SlateJsProxy = {
  isEditorOrState,
  createStateFromText,
  getPlainText,
  hasText,
  hasUnshortenedLinks,
  parseLinks,
  copyDraftEditorState,
  getFacebookAutocompleteEntities,
  flattenEditorStructure,

  getAllLinkedinAnnotations: (
    editor: BufferEditor,
  ): LinkedInAnnotationEntity[] => {
    return editor.getAllLinkedInAnnotations?.(editor) ?? []
  },
  insertLinkedInAnnotation: (
    editor: BufferEditor,
    annotation: LinkedInAnnotationEntityDetails,
  ): BufferEditor => {
    const command: unknown = Reflect.get(editor, 'insertLinkedInAnnotation')
    if (isInsertLinkedInAnnotation(command)) {
      Reflect.apply(command, editor, [editor, annotation])
    }

    return editor
  },

  replaceLinkWithShortLink: (
    editor: BufferEditor,
    link: string,
    shortLink: string,
  ): BufferEditor => {
    editor.replaceLinkWithShortLink?.(editor, link, shortLink)
    return editor
  },
  replaceLinkWithUnshortenedLink: (
    editor: BufferEditor,
    unshortenedLink: string,
    shortLink: string,
  ): BufferEditor => {
    editor.replaceLinkWithUnshortenedLink?.(editor, unshortenedLink, shortLink)
    return editor
  },

  insertSnippet: (editor: BufferEditor, text: string): BufferEditor => {
    const currentInlineNode = editor.api.above({
      match: (node) => !NodeApi.isEditor(node) && editor.api.isInline(node),
    })
    // const parent = getNodeParent(editor, currentNode)
    const isSelectionAtEndOfNode = isSelectionAtEndOfCurrentNode(
      editor,
      currentInlineNode,
    )

    // if the current node is an inline node and the selection is not at the end of the node,
    // don't do anything.
    // TODO: review what should happen when trying to insert inside an inline node like a link or mention
    if (currentInlineNode && !isSelectionAtEndOfNode) {
      return editor
    }

    // if the current node is an inline node and the selection is at the end of the node,
    // we need to move the selection by 1 to ensure the selection is outside the inline node
    if (currentInlineNode && isSelectionAtEndOfNode) {
      editor.tf.move({ distance: 1, unit: 'offset' })
    }

    const data = new DataTransfer()
    data.setData('text/plain', text)
    editor.tf.insertData(data)

    return editor
  },
  clearText: (editor: BufferEditor): BufferEditor => {
    const anchor = editor.api.start([])
    const focus = editor.api.end([])
    if (anchor && focus) editor.tf.select({ anchor, focus })
    editor.tf.deleteFragment()
    return editor
  },
  moveCursorToStart: (editor: BufferEditor): BufferEditor => {
    const editorStart = editor.api.start([])
    if (editorStart) {
      editor.tf.select({
        anchor: editorStart,
        focus: editorStart,
      })
    }
    return editor
  },
  /**
   * Clears the undo/redo history of a Plate editor.
   *
   * This is necessary when performing structural changes to the editor
   * (like adding/removing/switching threads) to prevent undo operations
   * from attempting to restore incompatible editor states that would
   * cause errors.
   */
  clearEditorHistory: (editor: BufferEditor): void => {
    // Plate editors with withHistory plugin store history in editor.history
    // with undos and redos arrays
    if (editor.history) {
      editor.history.undos = []
      editor.history.redos = []
    }
  },
  /**
   * Focuses the editor once React has committed the current editor tree to the
   * DOM.
   *
   * Focusing resolves `editor.selection` to a DOM range. Calling it
   * synchronously right after a structural state swap (e.g. add-post-to-thread,
   * which replaces the editor with a fresh empty node) races slate-react's
   * selection→DOM resolution: the new node has not been mounted yet, so
   * `ReactEditor.focus` throws "Cannot resolve a DOM node from Slate node".
   * Deferring to the next animation frame lets React render the new tree first,
   * so the caret still lands in the new node without throwing.
   */
  focus: (editor: BufferEditor): void => {
    requestAnimationFrame(() => {
      try {
        // Not editor.tf.focus(): v42's wrapper swallows DOMEditor's own
        // exceptions internally (just console.error), so this catch would
        // never see them. Calling DOMEditor.focus directly restores the
        // propagating behavior this catch depends on.
        DOMEditor.focus(editor as unknown as DOMEditor)
      } catch (error) {
        // The editor may have unmounted before the frame fired, or its selection
        // node may still be unresolvable to a DOM node. Focus is cosmetic, so
        // this race is safe to ignore rather than crash the composer. Any other
        // failure is unexpected and must stay observable.
        if (!isUnresolvedDomNodeError(error)) {
          Sentry.captureException(error, {
            tags: { method: 'SlateJsProxy.focus' },
          })
        }
      }
    })
  },
}

export type SlateJsProxyType = typeof SlateJsProxy
